// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, User, BarChart2, CreditCardIcon, Shield } from 'lucide-react'
import { AccountSettings } from './pages/AccountSettings'
import { Usage } from './pages/Usage'
import { BillingInvoices } from './pages/BillingInvoices'
import { AdminPrepaidCreditsPage } from './pages/AdminPrepaidCreditsPage'
import { getUserProfile } from '../../services/user'
import type { UserResponse } from 'models-ts/user'
import { useAuth } from '../../store/hooks/useAuth'
import { useTheme } from '../../hooks/useTheme'
import styles from './Dashboard.module.css'

type Tab = 'account' | 'usage' | 'billing' | 'admin'

export function Dashboard() {
    const [activeTab, setActiveTab] = useState<Tab>(() => getTabFromLocation())
    const [userProfile, setUserProfile] = useState<UserResponse | null>(null)
    const [isLoadingProfile, setIsLoadingProfile] = useState(true)
    const { user, loading: authLoading } = useAuth()
    const { t, i18n } = useTranslation('dashboard')
    const { hydrateTheme } = useTheme()
    const isAdmin = userProfile?.role === 'admin'
    // Admin status is unknown until auth hydrates and the profile fetch
    // resolves — deep links to ?tab=admin must not bounce before then.
    const isAdminPending = authLoading || (!!user && isLoadingProfile)

    useEffect(() => {
        if (user) {
            loadUserProfile()
        }
    }, [user])

    useEffect(() => {
        const handlePopState = () => setActiveTab(getTabFromLocation())
        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])

    // Hydrate i18n locale from user preferences on initial profile load only.
    // Using a ref prevents re-hydration from overriding explicit user changes
    // via the LanguageSwitcher (which saves to backend async).
    const hasHydratedLocale = useRef(false)
    useEffect(() => {
        if (!hasHydratedLocale.current && userProfile?.preferences?.locale && userProfile.preferences.locale !== i18n.language) {
            i18n.changeLanguage(userProfile.preferences.locale)
        }
        if (userProfile) {
            hasHydratedLocale.current = true
        }
    }, [userProfile, i18n])

    // Hydrate theme from user preferences on profile load
    useEffect(() => {
        if (userProfile?.preferences?.theme) {
            hydrateTheme(userProfile.preferences.theme)
        }
    }, [userProfile, hydrateTheme])

    const loadUserProfile = async () => {
        try {
            setIsLoadingProfile(true)
            const data = await getUserProfile()
            setUserProfile(data)
        } catch (err) {
            console.error('Failed to load user profile:', err)
        } finally {
            setIsLoadingProfile(false)
        }
    }

    const handleProfileUpdate = (updatedProfile: UserResponse) => {
        setUserProfile(updatedProfile)
    }

    const handleGoBack = () => {
        try {
            window.history.pushState({}, '', '/sessions')
            window.dispatchEvent(new PopStateEvent('popstate'))
        } catch {
            window.location.href = '/sessions'
        }
    }

    const handleSetActiveTab = useCallback(
        (tab: Tab) => {
            if (tab === 'admin' && !isAdmin) {
                return
            }
            setActiveTab(tab)
            const url = new URL(window.location.href)
            url.searchParams.set('tab', tab)
            window.history.replaceState({}, '', `${url.pathname}?${url.searchParams.toString()}`)
        },
        [isAdmin]
    )

    useEffect(() => {
        if (activeTab === 'admin' && !isAdminPending && !isAdmin) {
            handleSetActiveTab('account')
        }
    }, [activeTab, isAdmin, isAdminPending, handleSetActiveTab])

    return (
        <div className={styles.root}>
            <header className={styles.header}>
                <button onClick={handleGoBack} className={styles.backButton} aria-label={t('common:back')}>
                    <ChevronLeft />
                    <span>{t('common:back')}</span>
                </button>
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>{t('common:settings')}</h1>
                </div>
            </header>

            <div className={styles.tabs}>
                <button
                    onClick={() => handleSetActiveTab('account')}
                    className={`${styles.tab} ${activeTab === 'account' ? styles.activeTab : ''}`}
                >
                    <User className={styles.tabIcon} />
                    {t('profile')}
                </button>
                <button
                    onClick={() => handleSetActiveTab('usage')}
                    className={`${styles.tab} ${activeTab === 'usage' ? styles.activeTab : ''}`}
                >
                    <BarChart2 className={styles.tabIcon} />
                    {t('usage')}
                </button>
                <button
                    onClick={() => handleSetActiveTab('billing')}
                    className={`${styles.tab} ${activeTab === 'billing' ? styles.activeTab : ''}`}
                >
                    <CreditCardIcon className={styles.tabIcon} />
                    {t('billingHistory')}
                </button>
                {isAdmin && (
                    <button
                        onClick={() => handleSetActiveTab('admin')}
                        className={`${styles.tab} ${activeTab === 'admin' ? styles.activeTab : ''}`}
                    >
                        <Shield className={styles.tabIcon} />
                        {t('admin')}
                    </button>
                )}
            </div>

            <main className={styles.content}>
                {activeTab === 'account' && (
                    <AccountSettings
                        userProfile={userProfile}
                        isLoadingProfile={isLoadingProfile}
                        onProfileUpdate={handleProfileUpdate}
                    />
                )}
                {activeTab === 'usage' && <Usage />}
                {activeTab === 'billing' && <BillingInvoices />}
                {activeTab === 'admin' && isAdmin && <AdminPrepaidCreditsPage />}
            </main>
        </div>
    )
}

function getTabFromLocation(): Tab {
    const tab = new URLSearchParams(window.location.search).get('tab')
    if (tab === 'billing' || tab === 'usage' || tab === 'account' || tab === 'admin') {
        return tab
    }
    return 'account'
}
